import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { SearchableSelect } from "@/shared/components/common/SearchableSelect";
import { MultiSearchableSelect } from "@/shared/components/common/MultiSearchableSelect";

/** Single filter section config - reusable for different list types */
export interface FilterSectionConfig {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  /** Grid class for options layout, e.g. "grid grid-cols-3 gap-2" */
  optionsClassName?: string;
  disabled?: boolean;
  /** `text` renders a free-text input (`options` is ignored). */
  type?: "radio" | "searchable-select" | "multi-searchable-select" | "text";
  placeholder?: string;
  searchPlaceholder?: string;
}

export interface ListFilterValues {
  [sectionKey: string]: string | string[];
}

export interface ListFilterPopoverProps {
  /** e.g. [{ key: 'class', label: 'CLASS', options: [...] }, ...] */
  sections: FilterSectionConfig[];
  values: ListFilterValues;
  onChange: (values: ListFilterValues) => void;
  onApply: () => void;
  trigger: React.ReactNode;
  className?: string;
  /**
   * Optional cascade handler (e.g. Board → clears Class+Subject).
   * If provided, it receives current local values and should return next local values.
   */
  onSectionChange?: (
    key: string,
    value: string | string[],
    current: ListFilterValues,
  ) => ListFilterValues;

  /**
   * Optional callback for live draft changes (before Apply).
   * Useful for dynamic option loading (Board→Class→Subject) while popover is open.
   */
  onLocalValuesChange?: (values: ListFilterValues) => void;
}

/**
 * Reusable filter popover with multiple radio-group sections and Clear/Apply.
 * Use for teachers list (CLASS, SUBJECT, ACCOUNT STATUS) or students list with own sections.
 */
export function ListFilterPopover({
  sections,
  values,
  onChange,
  onApply,
  trigger,
  className,
  onSectionChange,
  onLocalValuesChange,
}: ListFilterPopoverProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [localValues, setLocalValues] = useState<ListFilterValues>(values);

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect --
       Intentionally reset draft filter values to the applied values when the popover opens. */
    if (open) {
      setLocalValues(values);
      onLocalValuesChange?.(values);
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open, values, onLocalValuesChange]);

  const handleSectionChange = (key: string, value: string | string[]) => {
    const base = { ...localValues, [key]: value };
    const next = onSectionChange ? onSectionChange(key, value, base) : base;
    setLocalValues(next);
    onLocalValuesChange?.(next);
  };

  const handleClear = () => {
    const cleared: ListFilterValues = {};
    sections.forEach((s) => {
      cleared[s.key] = s.type === "multi-searchable-select" ? [] : "";
    });
    setLocalValues(cleared);
    onLocalValuesChange?.(cleared);
    onChange(cleared);
    setOpen(false);
  };

  const handleApply = () => {
    onChange(localValues);
    onApply();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className={cn("w-[360px] max-w-[90vw] p-4 bg-white", className)}
        align="start"
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          {sections.map((section) => (
            <div key={section.key}>
              <Label className="text-xs font-bold uppercase text-[#333333]">
                {section.label}
              </Label>
              {section.type === "text" ? (
                <Input
                  className="mt-2"
                  value={String(localValues[section.key] ?? "")}
                  onChange={(e) => handleSectionChange(section.key, e.target.value)}
                  placeholder={section.placeholder}
                  disabled={section.disabled}
                  autoComplete="off"
                  aria-label={section.label}
                />
              ) : section.type === "searchable-select" ? (
                <div className={cn("mt-2", section.disabled && "opacity-50")}>
                  <SearchableSelect
                    value={String(localValues[section.key] ?? "")}
                    onValueChange={(v) => handleSectionChange(section.key, v)}
                    options={section.options}
                    placeholder={section.placeholder ?? t("actions.select")}
                    searchPlaceholder={section.searchPlaceholder ?? t("actions.search")}
                    viewAllLabel={t("actions.viewAll")}
                    triggerClassName={cn(
                      "h-9 rounded-md border border-[#E0E0E0] bg-white text-[#333333] hover:bg-[#FAFAFA]",
                      section.disabled && "pointer-events-none",
                    )}
                  />
                </div>
              ) : section.type === "multi-searchable-select" ? (
                <div className={cn("mt-2", section.disabled && "opacity-50")}>
                  <MultiSearchableSelect
                    value={
                      Array.isArray(localValues[section.key])
                        ? (localValues[section.key] as string[])
                        : []
                    }
                    onValueChange={(v) => handleSectionChange(section.key, v)}
                    options={section.options}
                    placeholder={section.placeholder ?? t("actions.select")}
                    searchPlaceholder={section.searchPlaceholder ?? t("actions.search")}
                    viewAllLabel={t("actions.viewAll")}
                    triggerClassName={cn(
                      "h-9 rounded-md border border-[#E0E0E0] bg-white text-[#333333] hover:bg-[#FAFAFA]",
                      section.disabled && "pointer-events-none",
                    )}
                  />
                </div>
              ) : (
                <RadioGroup
                  value={String(localValues[section.key] ?? "")}
                  onValueChange={(v) => handleSectionChange(section.key, v)}
                  disabled={section.disabled}
                  className={cn(
                    "mt-2 grid gap-2",
                    section.optionsClassName ?? "grid grid-cols-2 gap-2",
                    section.disabled && "opacity-50 pointer-events-none",
                  )}
                >
                  {section.options.map((opt) => (
                    <div
                      key={opt.value}
                      className="flex items-center space-x-2 px-4 py-2 rounded-full border border-[#D1D5DB]
             data-[state=checked]:bg-[#007BFF] data-[state=checked]:border-[#007BFF] transition-colors"
                    >
                      <RadioGroupItem
                        value={opt.value}
                        id={`${section.key}-${opt.value}`}
                        disabled={section.disabled}
                        className={cn(
                          "h-5 w-5 rounded-full border-2 border-[#D1D5DB] bg-white",
                          "data-[state=checked]:bg-[#007BFF] data-[state=checked]:border-[#007BFF]",
                          "flex items-center justify-center",
                        )}
                      >
                        <Check
                          className={cn(
                            "h-6 w-6 text-white bg-blue-300",
                            localValues[section.key] === opt.value
                              ? "block"
                              : "hidden",
                          )}
                        />
                      </RadioGroupItem>

                      <Label
                        htmlFor={`${section.key}-${opt.value}`}
                        className={cn(
                          "cursor-pointer text-sm font-normal",
                          localValues[section.key] === opt.value
                            ? "text-[#191919]"
                            : "text-[#191919B2]",
                        )}
                      >
                        {opt.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-md border-[#D1D5DB] bg-white text-[#333333] hover:bg-[#F9FAFB]"
            onClick={handleClear}
          >
            {t("actions.clear")}
          </Button>
          <Button
            type="button"
            size="sm"
            className="rounded-md bg-[#007BFF] text-white hover:bg-[#0056b3]"
            onClick={handleApply}
          >
            {t("actions.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
